// Auto-tag every interactive control on the page with a stable
// `data-issue-id` derived from its context. The picker in
// issue_log_controller.js then reads the attribute when the user
// clicks an element, so the admin gets a stable identifier they can
// jump to from the resolved issue thread.
//
// ID format:  "<section>.<controller>.<role>.<seq>"
//   section     – closest data-section value, or route-derived
//   controller  – first space-separated data-controller token
//   role        – button|link|input|select|textarea|control
//   seq         – 1-based index within the same section+controller scope
//
// Examples:
//   "about.dynamic-list.input.3"
//   "circleOfSupport.cos-relationship.select.2"
//   "_toolbar.editor-toolbar.button.7"
//
// We assign per-element rather than per-controller so two buttons
// inside the same controller still get distinct IDs.
//
// Re-runs after Turbo navigations and watches for added nodes via a
// MutationObserver so dynamically-rendered content (wizard step body,
// goal-resources rows, etc.) also gets tagged.

const INTERACTIVE_SEL = [
  "button", "a[href]", "[role=button]",
  "input", "textarea", "select",
  "[data-action]", "[data-controller]"
].join(",")

function sectionKeyFor(el) {
  const s = el.closest && el.closest("[data-section]")
  if (s && s.dataset.section) return s.dataset.section
  // Fall back to a route hint so non-editor pages still get a stable
  // bucket name in the tag.
  const path = (typeof window !== "undefined" && window.location ? window.location.pathname : "") || ""
  const m = path.match(/^\/(?:editor\/native\/([^\/?#]+)|([^\/?#]+))/)
  return (m && (m[1] || m[2])) || "root"
}

function controllerFor(el) {
  const c = el.closest && el.closest("[data-controller]")
  if (!c) return ""
  return (c.dataset.controller || "").split(/\s+/)[0] || ""
}

function roleFor(el) {
  const t = el.tagName.toLowerCase()
  if (t === "button" || el.matches("[role=button]")) return "button"
  if (t === "a")        return "link"
  if (t === "input")    return "input"
  if (t === "textarea") return "textarea"
  if (t === "select")   return "select"
  return "control"
}

// Per-page counters keyed by `section + controller + role` so the
// seq number is stable for the page load.
let counters = new Map()
function nextSeq(key) {
  const v = (counters.get(key) || 0) + 1
  counters.set(key, v)
  return v
}

function tagOne(el) {
  if (!el || el.nodeType !== 1) return
  if (el.dataset && el.dataset.issueId) return
  // Skip helper / picker chrome
  if (el.closest && el.closest(".issue-log-picker-banner, .issue-log-picker-outline, .issue-log-picker-label")) return
  const section    = sectionKeyFor(el) || "root"
  const controller = controllerFor(el) || el.tagName.toLowerCase()
  const role       = roleFor(el)
  const key        = `${section}.${controller}.${role}`
  const seq        = nextSeq(key)
  el.dataset.issueId = `${key}.${seq}`
}

function tagAll(root) {
  const scope = root || document
  if (scope.matches && scope.matches(INTERACTIVE_SEL)) tagOne(scope)
  scope.querySelectorAll && scope.querySelectorAll(INTERACTIVE_SEL).forEach(tagOne)
}

function resetForNewPage() {
  counters = new Map()
  // Clear stale IDs so the new page starts from seq 1 — the previous
  // page's IDs would still match selectors on a re-tag pass which
  // would skip them, but resetting keeps numbering tight.
  document.querySelectorAll("[data-issue-id]").forEach(el => { delete el.dataset.issueId })
  tagAll(document)
}

function startTaggingObserver() {
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes && m.addedNodes.forEach(n => { if (n.nodeType === 1) tagAll(n) })
    }
  })
  obs.observe(document.body, { childList: true, subtree: true })
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => { tagAll(document); startTaggingObserver() })
  } else {
    tagAll(document)
    startTaggingObserver()
  }
  // Turbo navigations replace the body — re-tag on each new page.
  document.addEventListener("turbo:load", resetForNewPage)
  document.addEventListener("turbo:frame-load", (e) => tagAll(e.target))
}

export { tagAll }

// ---------- Console error capture for issue reports ----------
// Wraps console.error / console.warn and listens for unhandled
// exceptions + Promise rejections, keeping the most recent N
// messages in a ring buffer. The issue picker reads
// `window.__pcpmConsoleLog` and ships it with every new issue so
// admins see real-time JS errors next to the user's description.
;(function () {
  if (typeof window === "undefined") return
  if (window.__pcpmConsoleLog) return  // re-run idempotent
  const MAX = 80
  const buf = []
  window.__pcpmConsoleLog = buf

  function push(level, args) {
    try {
      const text = Array.from(args).map(a => {
        if (a == null) return String(a)
        if (typeof a === "string") return a
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`
        try { return JSON.stringify(a) } catch (_) { return String(a) }
      }).join(" ")
      buf.push({ t: Date.now(), level, text: text.slice(0, 2000) })
      while (buf.length > MAX) buf.shift()
    } catch (_) { /* never let logging break itself */ }
  }

  const origErr  = console.error.bind(console)
  const origWarn = console.warn.bind(console)
  console.error = function () { push("error", arguments); origErr.apply(console, arguments) }
  console.warn  = function () { push("warn",  arguments); origWarn.apply(console, arguments) }

  window.addEventListener("error", (e) => {
    push("error", [`${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`, e.error?.stack || ""])
  })
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason
    const text = r instanceof Error ? `${r.name}: ${r.message}\n${r.stack || ""}` : String(r)
    push("unhandledrejection", [text])
  })
})()
